// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom'; // <-- Add this import
import { Container, Typography, Paper, Tabs, Tab, Button, Box, CircularProgress, Alert, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import LearningModules from './LearningModules';
import PsychometricTests from './PsychometricTests';
import Logo from './Logo';
import API_ENDPOINTS from '../config/apiConfig';
import { BranchProvider } from '../context/BranchContext';
import Hirees from './Hirees';
import Candidates from './Candidates';
import PersonalDocuments from './PersonalDocuments';
import AdminSettings from './AdminSettings';
import SuperadminPanel from './SuperadminPanel';
import Feedback from './Feedback';
import AdminFeedback from './AdminFeedback';
import FixedSnackbar from './FixedSnackbar';
import SupervisionPanel from './SupervisionPanel';
import TimecardPanel from './TimecardPanel';
import ReportForm from './ReportForm';
import AdminReports from './AdminReports';
import Vacancies from './Vacancies';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

function Dashboard({ isAuthenticated, role }) {
  const { lang, setLang, t } = useTranslation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [docsRequested, setDocsRequested] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSupervisionTab, setShowSupervisionTab] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const navigate = useNavigate();
  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountInfo = () => {
    handleMenuClose();
    navigate('/account-info');
  };

  const handleChangePassword = () => {
    handleMenuClose();
    navigate('/change-password');
  };

  const handleTabChange = (event, newValue) => {
    const newKey = availableTabs[newValue]?.key;
    if (newKey) {
      setSearchParams({ tab: newKey });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      const response = await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const translatedMessage = t(data.message) || data.message;
        setSnackbar({ open: true, message: translatedMessage, severity: 'success' });
        // Allow the snackbar to be visible briefly before navigating away
        setTimeout(() => {
          window.location.href = '/login';
        }, 800);
      } else {
        setSnackbar({ open: true, message: t('dashboard_logout_failed'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      setSnackbar({ open: true, message: t('dashboard_error_generic'), severity: 'error' });
    }
  };

  // Fetch the user's email and status on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/user/me`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (!userEmail) {
            setUserEmail(data.email);
          }
          setUserName(data.name || data.email);
          setUserStatus(data.status);
          setIsSupervisor(Boolean(data.is_supervisor));
        } else {
          console.error('Failed to fetch user info.');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserInfo();
  }, []);

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.SUBSCRIPTION_STATUS, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSubscriptionActive(data.active === true);
        } else {
          // If endpoint fails or doesn't exist yet, default to active (don't block)
          setSubscriptionActive(true);
        }
      } catch {
        setSubscriptionActive(true);
      }
    };
    fetchSubscription();
  }, []);

  // Determine whether to show the supervision tab for this user.
  useEffect(() => {
    if (['admin', 'superadmin'].includes(role)) {
      setShowSupervisionTab(true);
      return;
    }
    // For hirees, rely on the explicit isSupervisor flag returned by /api/user/me
    if (role === 'hiree') {
      setShowSupervisionTab(Boolean(isSupervisor));
      return;
    }
    setShowSupervisionTab(false);
  }, [role, isSupervisor]);

  // Fetch docs_requested status for candidate
  useEffect(() => {
    if (role === 'candidate' && userEmail) {
      fetch(`${API_ENDPOINTS.BASEURL}/api/user/${userEmail}`, { credentials: 'include' })
        .then(res => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.json();
        })
        .then(data => setDocsRequested(data.docs_requested === true))
        .catch(() => setDocsRequested(false));
    }
  }, [role, userEmail]);

  // Redirect if user is inactive
  useEffect(() => {
    console.log('userStatus:', userStatus); // Debug line
    if (userStatus === 'inactive') {
      navigate('/change-password');
    }
  }, [userStatus, navigate]);

  // Build the tabs array dynamically so indexes match Tabs order reliably
  const availableTabs = [];

  // Subscription gating: if subscription is not active, restrict tabs
  const subscriptionGated = subscriptionActive === false;

  if (subscriptionGated) {
    // When subscription is inactive: superadmin gets only the Superadmin tab; others get nothing
    if (role === 'superadmin') {
      availableTabs.push({ key: 'superadmin', label: t('superadmin_panel') || 'Superadmin', component: <SuperadminPanel /> });
    }
  } else if (['admin', 'superadmin'].includes(role)) {
    availableTabs.push({ key: 'vacancies', label: t('vacancies_panel'), component: <Vacancies /> });
    availableTabs.push({ key: 'candidates', label: t('candidates_panel'), component: <Candidates /> });
    availableTabs.push({ key: 'hirees', label: t('hiree_panel'), component: <Hirees /> });
    if (showSupervisionTab) availableTabs.push({ key: 'supervision', label: t('supervision_panel') || 'Supervision', component: <SupervisionPanel /> });
    availableTabs.push({ key: 'feedback', label: t('feedback_panel'), component: <AdminFeedback /> });
    availableTabs.push({ key: 'administration', label: t('administration'), component: <AdminSettings /> });
    if (role === 'superadmin') availableTabs.push({ key: 'superadmin', label: t('superadmin_panel') || 'Superadmin', component: <SuperadminPanel /> });
    availableTabs.push({ key: 'timecard', label: t('calendar_panel'), component: <TimecardPanel /> });
    availableTabs.push({ key: 'reports', label: t('reports_panel'), component: <AdminReports role={role} /> });
  } else if (role === 'candidate') {
    availableTabs.push({ key: 'psychometric', label: t('psychometrictests_panel'), component: userEmail ? <PsychometricTests userEmail={userEmail} /> : <Typography variant="body1" color="error">{t('dashboard_error_no_email_tests')}</Typography> });
    availableTabs.push({ key: 'personaldocs', label: t('personaldocuments_panel'), component: docsRequested ? (userEmail ? <PersonalDocuments userEmail={userEmail} /> : <Typography variant="body1" color="error">{t('dashboard_error_no_email_docs')}</Typography>) : <Typography variant="body1" color="warning">{t('dashboard_docs_not_requested')}</Typography> });
  } else if (role === 'hiree') {
    if (showSupervisionTab) availableTabs.push({ key: 'supervision', label: t('supervision_panel') || 'Supervision', component: <SupervisionPanel /> });
    availableTabs.push({ key: 'learningmodules', label: t('learningmodules_panel'), component: <LearningModules /> });
    availableTabs.push({ key: 'personaldocs', label: t('personaldocuments_panel'), component: <PersonalDocuments userEmail={userEmail} /> });
    availableTabs.push({ key: 'feedback', label: t('feedback_panel'), component: <Feedback /> });
    availableTabs.push({ key: 'timecard', label: t('calendar_panel'), component: <TimecardPanel /> });
    availableTabs.push({ key: 'report', label: t('report_panel'), component: <ReportForm /> });
  }

  // Get current tab from URL params
  const tabKey = searchParams.get('tab') || availableTabs[0]?.key || 'candidates';
  const tabIndex = availableTabs.findIndex(t => t.key === tabKey);
  const currentTab = tabIndex >= 0 ? tabIndex : 0;

  if (loading) {
    return (
      <Container maxWidth="md" style={{ marginTop: '30px', textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" style={{ marginTop: '20px' }}>
          {t('loading_dashboard_title')}...
        </Typography>
      </Container>
    );
  }

  return (
    <BranchProvider>
      <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 4 }, mb: { xs: 1, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper 
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 3, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(27, 73, 101, 0.03) 0%, rgba(98, 182, 203, 0.03) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(27, 73, 101, 0.08)'
        }}
      >
        {/* Language Selector */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <select value={lang} onChange={e => setLang(e.target.value)} style={{ padding: 4, marginRight: 8 }}>
            <option value="spanish">Español</option>
            <option value="english">English</option>
          </select>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 2, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: { xs: 0, sm: -6 } }}>
            <Logo width={isSmDown ? 120 : 240} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500, color: '#1B4965', display: { xs: 'none', sm: 'block' }, maxWidth: { sm: 160, md: 320 } }}>
              {userName}
            </Typography>
            <IconButton
              onClick={handleMenuOpen}
              size="large"
              sx={{ ml: 1 }}
              aria-label="account menu"
            >
              <Avatar sx={{ bgcolor: '#62B6CB' }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleAccountInfo}>
                <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('myaccount')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleChangePassword}>
                <ListItemIcon><VpnKeyIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('change_password')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('logout')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Paper elevation={3} sx={{ borderRadius: '15px', p: { xs: 1.5, sm: 2.5 } }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={availableTabs.length > 6 || isMdDown ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              allowScrollButtonsMobile
              centered={availableTabs.length <= 6 && !isMdDown}
            >
              {availableTabs.map((tab, idx) => (
                <Tab key={tab.key} value={idx} sx={{ textTransform: 'none', minWidth: { xs: 90, sm: 120 }, whiteSpace: 'nowrap', px: { xs: 1.5, sm: 2 } }} label={tab.label} />
              ))}
            </Tabs>
            <Box mt={3} sx={{ minHeight: '50vh' }}>
              {availableTabs.length === 0 && subscriptionGated ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('subscription_required_title') || 'Subscription Required'}
                  </Typography>
                  <Typography variant="body1">
                    {t('subscription_required_message') || 'Your Babordo subscription is not active. Please contact your administrator to renew.'}
                  </Typography>
                </Alert>
              ) : (
                availableTabs[currentTab] ? availableTabs[currentTab].component : null
              )}
            </Box>
          </Paper>
        </motion.div>
        <FixedSnackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </Paper>
      </Container>
    </BranchProvider>
  );
}

export default Dashboard;
